import {
  useGridApiContext,
  Toolbar,
  GridLocaleText,
  useGridSelector,
  gridFilterModelSelector,
  ExportCsv,
  ColumnsPanelTrigger,
  FilterPanelTrigger
} from '@mui/x-data-grid-pro';
import SaveIcon from '@mui/icons-material/Save';
import ResetIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { CustomQuickFilter } from './CustomQuickFilter';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { fileNameHelper } from '../../../utils/fileNameHelper';

export interface CustomToolbarProps {
  showSearch: boolean;
  title: string;
  saveState: () => Promise<boolean>;
  resetState: () => void;
  refreshState: () => void;
  downloadExcel?: () => void;
  hideSaveView?: boolean;
  hideExport?: boolean;
}

export const CustomToolbar = ({
  showSearch,
  title,
  saveState,
  resetState,
  refreshState,
  downloadExcel,
  hideSaveView = false,
  hideExport = false
}: CustomToolbarProps) => {
  const { setSnack } = useSnackbar();
  const apiRef = useGridApiContext();

  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  const handleDensityChange = (density: 'compact' | 'standard' | 'comfortable') => {
    apiRef.current.setDensity(density);
    setDensityMenuAnchor(null);
  };
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterItems = filterModel?.items || [];
  const activeFilters = filterItems.filter((item) => {
    const hasValue = item.value !== undefined && item.value !== null && item.value !== '';

    return hasValue || ['isEmpty', 'isNotEmpty'].includes(item.operator);
  });
  const columnsLookup = apiRef.current.state.columns?.lookup || {};

  const getOperatorLabel = (operator: string): string => {
    const key = `filterOperator${operator.charAt(0).toUpperCase()}${operator.slice(1)}` as keyof GridLocaleText;
    return (apiRef.current.getLocaleText?.(key) as string) ?? operator;
  };

  const getFormattedFilterValue = (item: {
    field: string;
    operator: string;
    value?: string | number | boolean | null | undefined | Date;
  }): string => {
    if (['isEmpty', 'isNotEmpty'].includes(item.operator)) return '';

    const colDef = columnsLookup[item.field];
    if (!colDef) return String(item.value ?? '');

    try {
      if (typeof colDef.valueFormatter === 'function' && item.value) {
        return String((colDef.valueFormatter as (value: string) => Promise<void>)(String(item.value)));
      }
    } catch {
      // ignored intentionally
    }
    return String(item.value ?? '');
  };

  const tooltipTitle = activeFilters.length ? (
    <>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 'bold',
          mb: 0.5
        }}>
        {`${activeFilters.length} active filter${activeFilters.length > 1 ? 's' : ''}`}
      </Typography>
      <List
        dense
        disablePadding>
        {activeFilters.map((item, idx) => (
          <ListItem
            key={idx}
            disableGutters
            disablePadding>
            <ListItemText
              primary={`
                ${idx + 1}.
                ${columnsLookup[item.field]?.headerName ?? item.field}
                ${getOperatorLabel(item.operator)}
                ${getFormattedFilterValue(item)}
              `}
              slotProps={{
                primary: {
                  variant: 'body2'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </>
  ) : (
    'No active filters'
  );

  return (
    <Toolbar data-testid="grid-toolbar">
      <Box
        sx={{
          padding: '1rem 0rem 1rem 1rem',
          gap: 1,
          flexWrap: 'wrap',
          display: 'flex',
          alignItems: 'left',
          width: '100%'
        }}>
        {showSearch && <CustomQuickFilter />}
        <Box sx={{ marginLeft: showSearch ? 'auto' : 0, display: 'flex', gap: 0, flexWrap: 'wrap' }}>
          {!hideSaveView && (
            <Tooltip
              title="Save View"
              enterDelay={200}>
              <Button
                sx={{ fontSize: '.8rem' }}
                onClick={async () => {
                  // The refusal message the grid shows on a rejected save must not be overwritten by a success one.
                  if (await saveState())
                    setSnack({ message: 'Your view settings were saved successfully', type: 'success', open: true });
                }}
                color="secondary"
                startIcon={<SaveIcon />}>
                Save View
              </Button>
            </Tooltip>
          )}

          <Tooltip
            title="Select columns"
            enterDelay={200}>
            <ColumnsPanelTrigger
              render={
                <Button
                  startIcon={<ViewColumnIcon fontSize="small" />}
                  sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                  Columns
                </Button>
              }
            />
          </Tooltip>

          <Tooltip
            title={tooltipTitle}
            enterDelay={200}>
            <FilterPanelTrigger
              render={
                <Button
                  startIcon={
                    <Badge
                      badgeContent={activeFilters.length}
                      color="primary"
                      invisible={activeFilters.length === 0}>
                      <FilterListIcon fontSize="small" />
                    </Badge>
                  }
                  sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                  Filters
                </Button>
              }
            />
          </Tooltip>

          <Tooltip
            title="Density"
            enterDelay={200}>
            <Button
              startIcon={<FormatLineSpacingIcon fontSize="small" />}
              onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
              sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
              Density
            </Button>
          </Tooltip>

          <Menu
            anchorEl={densityMenuAnchor}
            open={Boolean(densityMenuAnchor)}
            onClose={() => setDensityMenuAnchor(null)}>
            <MenuItem onClick={() => handleDensityChange('compact')}>
              <FormatAlignJustifyIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Compact
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('standard')}>
              <DensityMediumIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Standard
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('comfortable')}>
              <DensitySmallIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Comfortable
            </MenuItem>
          </Menu>

          {!hideExport && !downloadExcel && (
            <Tooltip
              title="Export To CSV"
              enterDelay={200}>
              <ExportCsv
                options={{ fileName: fileNameHelper(title) }}
                render={
                  <Button
                    startIcon={<FileDownloadIcon fontSize="small" />}
                    sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                    Export
                  </Button>
                }
              />
            </Tooltip>
          )}

          {!hideExport && downloadExcel && (
            <Tooltip
              title="Export"
              enterDelay={200}>
              <Button
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                Export
              </Button>
            </Tooltip>
          )}

          {!hideExport && (
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  downloadExcel && downloadExcel();
                  setExportMenuAnchor(null);
                }}
                sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                Download as Excel
              </MenuItem>
              <ExportCsv
                onClick={() => {
                  setExportMenuAnchor(null);
                }}
                options={{ fileName: fileNameHelper(title) }}
                render={<MenuItem sx={{ fontSize: '.8rem', color: 'secondary.main' }}>Download as CSV</MenuItem>}
              />
            </Menu>
          )}

          <Tooltip
            title="Reset Table"
            enterDelay={200}>
            <Button
              sx={{ fontSize: '.8rem', color: 'secondary.main' }}
              onClick={() => resetState()}
              startIcon={<ResetIcon fontSize="small" />}>
              Reset
            </Button>
          </Tooltip>

          <Tooltip
            title="Refresh Table"
            enterDelay={200}>
            <Button
              sx={{ fontSize: '.8rem', color: 'secondary.main' }}
              onClick={() => refreshState()}
              startIcon={<RefreshIcon fontSize="small" />}>
              Refresh
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Toolbar>
  );
};
